

/**
 * @module CustomButton/CustomButton
 */

import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview';

/**
 * The simple button plugin. It makes buttons in the toolbar configurable from outside.
 *
 * @extends module:core/plugin~Plugin
 */
export default class CustomButton extends Plugin {

    /**
     * @inheritDoc
     */
    static get requires() {
        return [CustomButtonUi];
    }

    /**
     * @inheritDoc
     */
    static get pluginName() {
        return 'customButton';
    }

}

export class CustomButtonUi extends Plugin {

    /**
     * @inheritDoc
     */
    init() {
        this.buttons = [];
        const buttonDefinitions = this.editor.config.get('customButton') || [];
        buttonDefinitions.forEach(definition => this.createToolbarButton(definition));

        this.editor.on('change:isReadOnly', () => {
            this.buttons.forEach(button => {
                if (button._syncDisabledState) {
                    button.isEnabled = !this.editor.isReadOnly;
                }
            });
        });
    }

    /**
     * Creates a toolbar Button. Clicking this button will execute
     * the specified callback.
     *
	 * @param buttonDefinition the button configuration
     * @private
     */
    createToolbarButton(buttonDefinition) {
        const editor = this.editor;

        editor.ui.componentFactory.add(buttonDefinition.name, locale => {
            const button = this.createButton(locale, buttonDefinition);
            button.isEnabled = true;
            button._syncDisabledState = typeof buttonDefinition.syncDisabledState === "boolean" ? buttonDefinition.syncDisabledState : true;

            // Change enabled state and execute the specific callback on click.
            this.listenTo(button, 'execute', () => {
                this.enableButton(button, false);
                Promise.resolve(buttonDefinition.onClick(button, editor)).then(() => this.enableButton(button, true)).catch(() => this.enableButton(button, true));
            });

            this.buttons.push(button);
            return button;
        });
    }

	/**
     * Enables or disables the button due to the button config
	 * and the current readOnly-state of the editor.
     *
	 * @param button the buttonView
	 * @param value new enabled state
     * @private
     */
    enableButton(button, value) {
        if (button._syncDisabledState) {
            button.isEnabled = !this.editor.isReadOnly && value;
        } else {
            button.isEnabled = value;
        }
    }

	/**
     * Internal creation method of ButtonView objects
     *
	 * @param label the button label (string)
	 * @param icon the button icon (string)
	 * @param icon the button locale (string)
     * @private
     */
    createButton(locale, { label, icon, withText = false, tooltip = true }) {
        const button = new ButtonView(locale);

        button.set({
            label,
            icon,
            withText,
            tooltip,
        });

        return button;
    }
}